import type { TabNavProps } from './TabNav.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { Box } from '../Box';
import { styled } from '@withjoy/joykit/theme';
import { anatomy } from '@shared/joykit/packages/core/common/utils/anatomy';
import type { MultiPartStyleConfig, PartsStyleObject } from '@shared/joykit/packages/core/common/utils/styleConfig';
import { pxToRem } from '@shared/joykit/packages/core/common/themes/shared';

export const StyledRoot = styled(Box)``;

export const StyledList = styled(Box)``;

export const StyledItem = styled(Box)``;

export const StyledLink = styled(Box)``;

export const StyledIndicator = styled(Box)``;

export const StyledMenuTrigger = styled(Box)``;

export const StyledBadge = styled(Box)``;

const tabNavAnatomy = anatomy('TabNav').parts('root', 'list', 'item', 'link', 'indicator', 'menuTrigger', 'badge');

const tabNavBaseStyles = ({ variant = 'default', indicatorStyle = 'underline' }: TabNavProps): PartsStyleObject<typeof tabNavAnatomy> => {
  const isNative = variant === 'native';
  const isPillStyle = indicatorStyle === 'pill';

  // Get indicator styles based on variant and style
  const getIndicatorStyles = () => {
    if (isNative) {
      // Native variant: sliding white pill background behind active tab
      return {
        position: 'absolute' as const,
        top: '50%',
        left: 0,
        height: pxToRem(40),
        transform: 'translateY(-50%)',
        backgroundColor: 'white',
        borderRadius: '200px',
        transitionProperty: animationTransition('transform', 'width', { duration: '300ms' }),
        transitionDuration: '300ms',
        pointerEvents: 'none' as const,
        zIndex: 0,
      };
    }

    if (isPillStyle) {
      // Pill style (default variant): sliding gray pill background behind active tab
      return {
        position: 'absolute' as const,
        top: '50%',
        left: 0,
        height: pxToRem(36),
        transform: 'translateY(-50%)',
        backgroundColor: 'mono2',
        borderRadius: pxToRem(8),
        transitionProperty: animationTransition('transform', 'width', { duration: '300ms' }),
        transitionDuration: '300ms',
        pointerEvents: 'none' as const,
        zIndex: 0,
      };
    }

    // Default variant (underline): sliding underline indicator with rounded corners
    return {
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      height: '2px',
      backgroundColor: 'mono14',
      borderRadius: '2px',
      transitionProperty: animationTransition('transform', 'width', { duration: '300ms' }),
      transitionDuration: '300ms',
      pointerEvents: 'none' as const,
    };
  };

  return {
    root: {
      display: 'flex',
      alignItems: 'center',
    },
    list: {
      display: 'flex',
      alignItems: 'center',
      gap: isNative ? 0 : 2,
      height: isNative ? pxToRem(52) : pxToRem(48),
      padding: isNative ? 1 : 2,
      borderWidth: isNative ? '6px' : '0px',
      borderColor: isNative ? 'mono2' : 'mono3',
      borderStyle: isNative ? 'solid' : 'none',
      borderBottomStyle: 'solid',
      borderBottomWidth: isNative ? '6px' : '1px',
      borderRadius: isNative ? '200px' : undefined,
      backgroundColor: isNative ? 'mono2' : undefined,
      position: 'relative',
      margin: 0,
      listStyle: 'none',
    },
    item: {
      display: 'flex',
      flex: '0 0 auto',
      position: 'relative',
    },
    link: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingX: isNative ? 5 : 4,
      color: isNative ? 'mono14' : 'mono10',
      textDecoration: 'none',
      typographyVariant: isNative ? 'body3' : 'button1',
      height: isNative ? pxToRem(40) : pxToRem(36),
      borderRadius: isNative ? '200px' : pxToRem(8),
      backgroundColor: isNative ? 'transparent' : undefined,
      transitionProperty: animationTransition('color', 'background-color'),
      transitionDuration: '250ms',
      _hover: isNative
        ? {}
        : {
            backgroundColor: 'mono2',
            color: 'mono14',
          },
      _focus: {
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.4)',
        borderRadius: isNative ? '200px' : pxToRem(8),
      },
      _focusVisible: {
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.4)',
        borderRadius: isNative ? '200px' : pxToRem(8),
      },
    },
    indicator: getIndicatorStyles(),
    menuTrigger: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      paddingX: 4,
      color: isNative ? 'mono14' : 'mono10',
      height: isNative ? pxToRem(40) : pxToRem(36),
      borderRadius: isNative ? '200px' : pxToRem(8),
      background: 'none',
      border: 'none',
      typographyVariant: isNative ? 'body3' : 'button1',
      transitionProperty: animationTransition('color', 'background-color'),
      transitionDuration: '250ms',
      _hover: isNative
        ? {}
        : {
            backgroundColor: 'mono2',
            color: 'mono14',
          },
      _focus: {
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.4)',
        borderRadius: isNative ? '200px' : pxToRem(8),
      },
    },
    badge: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  };
};

/**
 * Styles for active state - applied conditionally
 * @param isActive - Whether this tab is currently active
 * @param isNative - Whether using the 'native' variant
 * @param isPillStyle - Whether using 'pill' indicator style (default variant only)
 */
export const getActiveLinkStyles = (isActive: boolean, isNative: boolean, isPillStyle = false) => {
  if (!isActive) return {};

  if (isNative) {
    // Native variant: just change text color, background is handled by sliding indicator
    return {
      color: 'black',
      position: 'relative' as const,
      zIndex: 1,
    };
  }

  if (isPillStyle) {
    // Pill style: text color changes, sliding indicator provides background
    return {
      color: 'mono14',
      position: 'relative' as const,
      zIndex: 1,
      // No background - the sliding indicator provides it
      backgroundColor: 'transparent',
      _hover: {
        // Keep hover transparent since indicator handles it
        backgroundColor: 'transparent',
      },
    };
  }

  // Default variant (underline): active tabs have mono14 text, no background (only the underline indicator)
  return {
    color: 'mono14',
    backgroundColor: 'transparent',
    _hover: {
      backgroundColor: 'transparent',
    },
  };
};

/**
 * Styles for inactive tabs
 * - Native: mono10 text, no background (indicator provides active background)
 * - Pill style: mono10 text, keep hover background
 * - Default: mono10 text (already set in base styles)
 */
export const getInactiveNativeLinkStyles = (isNative: boolean, isPillStyle = false) => {
  if (isNative) {
    return {
      color: 'mono10',
      position: 'relative' as const,
      zIndex: 1,
    };
  }

  if (isPillStyle) {
    // Pill style inactive tabs: position above indicator but keep normal styling
    return {
      position: 'relative' as const,
      zIndex: 1,
    };
  }

  return {};
};

export const tabNavStyles: MultiPartStyleConfig<TabNavProps, typeof tabNavAnatomy> = {
  parts: tabNavAnatomy.keys,
  baseStyle: tabNavBaseStyles,
};

export const DEFAULT_MENU_ITEM_STYLES = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  paddingX: 4,
  paddingY: 3,
  cursor: 'pointer',
  color: 'mono14',
  typographyVariant: 'body1' as const,
  textDecoration: 'none',
  width: '100%',
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  textAlign: 'left' as const,
  _hover: {
    backgroundColor: 'mono2',
  },
};

export const DEFAULT_BADGE_STYLES = {
  color: 'white',
  backgroundColor: 'brandPrimary',
  borderRadius: '50px',
  paddingX: 2,
  paddingY: '3px',
  typographyVariant: 'label3' as const,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

export const DEFAULT_SUBHEAD_STYLES = {
  display: 'flex',
  alignItems: 'center',
  paddingX: 4,
  paddingY: 2,
  color: 'mono10',
  typographyVariant: 'label2' as const,
  width: '100%',
  cursor: 'default',
};

export const DEFAULT_DIVIDER_STYLES = {
  height: '1px',
  backgroundColor: 'mono3',
  marginY: 2,
};
